<template>
  <div class="bulk-invite">
    <div class="table-row md-elevation-3">
      <toolbar
        class="new-globals"
        title="Invitations"
        :has-search="false"
        :has-menu="false"
      />
      <md-progress-bar v-if="inviting" md-mode="indeterminate" />
      <md-table
        :value="state.users"
        md-sort="name"
        md-sort-order="asc"
        md-fixed-header
      >
        <md-table-row slot="md-table-row" slot-scope="{ item }">
          <md-table-cell md-label="Email" md-sort-by="email">{{ item.email }}</md-table-cell>
          <md-table-cell md-label="Role" md-sort-by="role">
            <md-menu md-direction="bottom-start" :md-close-on-select="true">
              <md-button md-menu-trigger>
                {{ roleLabels[item.role] }}
                <md-icon>arrow_drop_down</md-icon>
              </md-button>
              <md-menu-content>
                <md-menu-item v-if="canSetRole('ADMIN')" @click="selectRole(item, 'ADMIN')">
                  {{ roleLabels['ADMIN'] }}
                </md-menu-item>
                <md-menu-item v-if="canSetRole('USER')" @click="selectRole(item, 'USER')">
                  {{ roleLabels['USER'] }}
                </md-menu-item>
                <md-menu-item v-if="canSetRole('STUDENT')" @click="selectRole(item, 'STUDENT')">
                  {{ roleLabels['STUDENT'] }}
                </md-menu-item>
              </md-menu-content>
            </md-menu>
          </md-table-cell>
          <md-table-cell md-label="Remove" width="50px">
            <md-button class="md-icon-button" @click="removeUser(item)">
              <md-icon>close</md-icon>
            </md-button>
          </md-table-cell>
        </md-table-row>
      </md-table>
    </div>
    <div class="email-group-row">
      <div class="add-field">
        <md-field :class="messageClass">
          <md-input v-model="state.email" placeholder="Add by Email" @keydown.enter="addUser" />
          <md-button class="md-icon-button" @click="addUser">
            <md-icon>add</md-icon>
          </md-button>
          <span class="md-error">{{ state.emailErrorStr }}</span>
        </md-field>
      </div>
      <div class="group-field">
        <md-field>
          <md-select
            placeholder="Group Assignment"
            :multiple="true"
            :value="state.selectedGroupIds"
            :disabled="groups.length === 0"
            @md-selected="updateSelectedGroups"
          >
            <md-option
              v-for="group in groups"
              :key="group._id"
              :value="group._id"
            >
              {{ stripPrefix(group.name) }}
            </md-option>
          </md-select>
          <md-tooltip md-direction="bottom" md-delay="300">
            Invited users will automatically be added to these groups.
          </md-tooltip>
        </md-field>
      </div>
    </div>
    <div class="button-row">
      <div class="upload-section">
        <md-button class="md-raised md-primary" @click="openFileSelector">
          Upload CSV List
        </md-button>
        <help-tooltip direction="bottom" type="help">
          Upload a CSV/Excel file with a list of users.<br>
          One or more columns of users can be entered.<br>
          The first row of each column should be a header<br>
          such as 'Email', 'First Name', or 'Last Name'
        </help-tooltip>
        <input
          ref="fileInput"
          type="file"
          style="visibility: hidden"
          accept=".xls,.xlsx,.csv"
          @change="handleFileChange"
        >
        <span v-if="state.xlsx.error" class="red-3 color">{{ state.xlsx.error }}</span>
      </div>
      <div class="button-with-message">
        <md-button
          :disabled="state.users.length === 0 || notEnoughSeats || inviting"
          class="md-raised md-primary"
          @click="submit"
        >
          Submit Invitations
        </md-button>
        <span v-if="notEnoughSeats" class="red-3 color">
          Invitations exceed open seats for your organization.  Reduce invites or request more seats.
        </span>
        <div class="error-row red-3 color">
          <span v-if="inviteError">{{ inviteError.message || inviteError.error || inviteError }}</span>
        </div>
      </div>
    </div>

    <!-- Column selection modal -->
    <md-dialog :md-active.sync="state.xlsx.columnModalOpen" :md-fullscreen="false">
      <md-dialog-title>Select Column</md-dialog-title>
      <md-dialog-content>
        <md-field>
          <md-select
            :value="state.xlsx.selectedColumn"
            @input="(col) => selectColumn(col)"
          >
            <md-option
              v-for="(col, idx) in state.xlsx.columns"
              :key="col + idx"
              :value="col"
            >
              {{ col }}
            </md-option>
          </md-select>
        </md-field>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-primary" @click="state.xlsx.columnModalOpen = false">Cancel</md-button>
        <md-button class="md-primary" @click="importEmails">OK</md-button>
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>

<script>
import Toolbar from '~lib/common/components/Toolbar';
import HelpTooltip from '~lib/common/components/HelpTooltip';
import { stripPrefix } from '../../common/GroupName';
import { read } from './xlsxReader';

const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/;

export default {
  name: 'bulk-invite',
  components: {
    Toolbar,
    HelpTooltip,
  },
  props: {
    user: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    org: {
      type: Object,
      default: () => null,
    },
    groups: {
      type: Array,
      default: () => [],
    },
    error: {
      type: Object,
      default: null,
    },
    inviting: {
      type: Boolean,
      default: false
    },
    inviteError: {
      type: Object,
      default: null
    },
  },
  data() {
    return {
      state: {
        xlsx: {
          file: null,
          columns: [],
          rows: [],
          selectedColumn: null,
          error: null,
          columnModalOpen: false,
          importedEmails: [],
        },
        email: '',
        isEmailError: false,
        emailErrorStr: '',
        users: [],
        selectedGroupIds: [],
        confirmModalOpen: false,
        invitesCompleteModalOpen: false,
      },
      roleLabels: {
        ADMIN: 'Admin',
        SUPER_ADMIN: 'Super Administrator',
        USER: 'Instructor',
        STUDENT: 'Student'
      },
    }
  },
  computed: {
    messageClass() {
      return {
        'md-invalid': this.state.isEmailError,
      };
    },
    notEnoughSeats() {
      return (this.org.metadata.memberCount + this.state.users.length) > this.org.metadata.seats;
    },
  },
  methods: {
    addUser() {
      this.state.isEmailError = false;

      const email = this.state.email.trim().toLowerCase();
      if (!this.isEmailValid(email)) {
        this.showEmailError('Invalid email address', 2500);
        return;
      }

      if (this.isEmailInList(email)) {
        this.showEmailError('Already in list', 2500);
        return;
      }

      this.state.users.push({
        email: email,
        role: 'STUDENT',
      });
      this.state.email = '';
    },
    showEmailError(msg, duration) {
      this.state.isEmailError = true;
      this.state.emailErrorStr = msg;
      setTimeout(() => {
        this.state.isEmailError = false;
      }, duration);
    },
    removeUser(user) {
      this.state.users = this.state.users.filter(u => u.email != user.email);
    },
    updateSelectedGroups(groupIds) {
      this.state.selectedGroupIds = groupIds;
    },
    isEmailValid(email) {
      return regex.test(email);
    },
    isEmailInList(email) {
      return this.state.users.find(
        user => user.email.toLowerCase() === email.toLowerCase()
      );
    },
    handleFileChange(e) {
      const files = e.target.files, f = files[0];
      if (files.length > 0) {
        this.state.xlsx.file = f;
        this.getColumns();
      }
      this.$refs.fileInput.value = '';
    },
    openFileSelector() {
      this.$refs.fileInput.click();
    },
    async getColumns() {
      try {
        this.state.xlsx.error = null;
        const result = await read(this.state.xlsx.file);
        this.state.xlsx.columns = result.columns;
        if (result.rows.length > 50) {
          this.state.xlsx.rows = result.rows.slice(0, 50);
          this.state.xlsx.error = 'Can only load first 50 emails';
        } else {
          this.state.xlsx.rows = result.rows;
        }
        const count = this.state.xlsx.columns.length;
        if (count > 1) {
          // Two or more columns, show selection dialog
          this.state.xlsx.columnModalOpen = true;
        } else if (count == 1) {
          // Only one column, so directly import
          this.state.xlsx.selectedColumn = this.state.xlsx.columns[0];
          this.importEmails();
        } else {
          this.state.xlsx.error = 'Unable to find valid emails in file';
        }
      } catch(e) {
        this.state.xlsx.error = e;
      }
    },
    selectColumn(column) {
      this.state.xlsx.selectedColumn = column;
    },
    async importEmails() {
      this.state.xlsx.columnModalOpen = false;
      const file = this.state.xlsx.file;
      let emailsAdded = false;

      this.state.xlsx.rows.forEach(row => {
        const email = row[this.state.xlsx.selectedColumn].trim().toLowerCase();
        if (email && this.isEmailValid(email) && !this.isEmailInList(email)) {
          this.state.users.push({ email, role: 'STUDENT' });
          emailsAdded = true;
        }
      })

      if (!emailsAdded) {
        this.state.xlsx.error = 'Unable to find valid emails in file';
      }
    },
    selectRole(user, role) {
      user.role = role;
    },
    canSetRole(role = 'USER') {
      const roleHierarchy = {
        SUPER_ADMIN: 4,
        ADMIN: 3,
        USER: 2,
        STUDENT: 1
      };
      const userRole = this.user['https://vrpatients.com/role'];
      if (roleHierarchy[userRole] >= roleHierarchy[role]) {
        return true;
      }
      return false;
    },
    submit() {
      this.confirmSubmit();
    },
    confirmSubmit() {
      this.$emit('submit',
        this.state.users,
        this.state.selectedGroupIds,
        this.org);
    },
    stripPrefix,
  },
}
</script>

<style scoped>
.bulk-invite {
  overflow: auto;
  padding: 0 12px;
}

.error-row {
  margin-bottom: 12px;
}

.table-row {
  min-height: 200px;
  min-width: 500px;
}

.email-group-row {
  margin-top: 12px;
  display: flex;
  flex-direction: row;
}

.add-field {
  margin-right: 12px;
  flex: 3;
}

.group-field {
  flex: 2;
}

.add-field .md-field {
  position: relative;
}

.add-field .md-button {
  position: absolute;
  right: 0px;
  top: 6px;
}

.button-row {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.button-row .md-button {
  margin: 0 0 8px 0;
}

.md-menu .md-button {
  text-transform: none;
  font-size: 18px;
  font-weight: inherit;
}

.button-with-message {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
}
</style>